// 销售应收管理页的静态常量：状态/类型中文映射、下拉选项。
// 从 SalesRevenueManagementPage.jsx 抽出，纯数据、无 React 依赖。

import { CARGO_AIRCRAFT_TYPE_OPTIONS } from "../../../constants/aircraftTypes";

export const INTERNAL_SECTIONS = [
    { key: "routes", label: "销售航线池" },
    { key: "contracts", label: "销售合同" },
    { key: "receivables", label: "应收与收款" },
];

export const ROUTE_STATUS_TEXT = {
    ACTIVE: "启用",
    INACTIVE: "停用",
};

export const ROUTE_SOURCE_TEXT = {
    MANUAL: "手工维护",
    SALES_CONTRACT: "销售合同",
    SCHEDULE: "排班引用",
    HISTORICAL_FLIGHT: "历史航班",
};

export const CONTRACT_STATUS_TEXT = {
    DRAFT: "草稿",
    ACTIVE: "生效中",
    EXPIRED: "已过期",
    TERMINATED: "已终止",
};

export const CONTRACT_KIND_TEXT = {
    TIER: "阶梯合同",
    PER_LEG_BURN: "按段油耗",
    AD_HOC: "一班一议",
    ACMI: "ACMI（时段包机）",
};

export const CONTRACT_KIND_OPTIONS = [
    { value: "TIER", label: "阶梯合同（按油价偏差档位固定金额）" },
    { value: "PER_LEG_BURN", label: "按段油耗（逐段月均价 × 油耗）" },
    { value: "AD_HOC", label: "一班一议（合同配航线价，燃油选手填）" },
    { value: "ACMI", label: "ACMI（时段包机）" },
];

export const CREDIT_STATUS_TEXT = {
    ACTIVE: "可用",
    EXHAUSTED: "已用尽",
    CANCELLED: "已取消",
};

export const BOOKING_STATUS_TEXT = {
    ACTIVE: "生效中",
    COMPLETED: "已完成",
    CANCELLED: "已取消",
};

export const RECEIVABLE_STATUS_TEXT = {
    PENDING_INVOICE: "待开票",
    INVOICED: "已开票",
    PARTIALLY_PAID: "部分收款",
    PAID: "已收款",
    CANCELLED: "已取消",
    EXCEPTION: "匹配异常",
};

export const FLIGHT_OP_STATUS_TEXT = {
    SCHEDULED: "已计划",
    ACTIVE: "执行中",
    DELAYED: "延误",
    DEPARTED: "已起飞",
    COMPLETED: "已完成",
    DIVERTED: "备降",
    CANCELLED: "已取消",
    PLAN_CANCELLED: "计划取消",
    REPLACED: "已替换",
};

export const RECEIVABLE_TYPE_TEXT = {
    BASE_FARE: "基础运价",
    FUEL_SURCHARGE: "燃油调整",
    EXTRA_FEE: "额外费用",
};

export const INVOICE_STATUS_TEXT = {
    ISSUED: "已开票",
    PARTIALLY_PAID: "部分收款",
    PAID: "已收款",
    VOIDED: "已作废",
};

export const INVOICE_TYPE_TEXT = {
    BASE_WEEKLY: "基础运价预付发票",
    FUEL_MONTHLY: "燃油调整发票",
    EXTRA_FEE: "额外费用发票",
    MIXED: "混合发票",
};

export const PAYMENT_SOURCE_TYPE_TEXT = {
    CUSTOMER_PAYMENT: "客户打款",
    CREDIT_LEDGER: "余额抵扣",
};

export const CREDIT_SOURCE_TYPE_TEXT = {
    PREPAYMENT: "预付款",
    CANCELLED_BOOKING: "取消航班抵扣",
};

export const RECEIVABLE_EXCEPTION_TEXT = {
    CONTRACT_MISSING: "未绑合同",
    CUSTOMER_MISSING: "缺少客户",
    NO_ACTIVE_CONTRACT: "未匹配合同",
    ROUTE_PRICE_NOT_FOUND: "未匹配价格",
    AIRCRAFT_TYPE_INCONSISTENT: "机型不一致",
    MULTIPLE_ACTIVE_CONTRACTS: "多合同重叠",
    PRICE_CONFLICT: "价格冲突",
    BOOKING_FLIGHT_NOT_FOUND: "航段缺失",
    NO_BILLABLE_LEGS: "没有还要算的航段",
    FLIGHT_LEGS_DISCONNECTED: "航段接不上",
    FLIGHT_PATTERN_UNDETERMINED: "看不出是直飞还是有经停",
    FLIGHT_PATTERN_SOURCE_MISMATCH: "起点终点和飞法不是同一批航段",
};

export const RECEIVABLE_WARNING_TEXT = {
    FLIGHT_DATE_OUT_OF_CONTRACT_PERIOD: "日期超出合同期",
    PINNED_ROUTE_PRICE_MISMATCH: "钉住价不配套，已按现查生成",
    NO_LEG_SINGLE_FARE: "没有航段，飞法未判，已按唯一成交价生成",
};

// 应收「价格匹配」来源的可读文案：只收录需要给用户换词的值（订单钉住的价），
// 未收录的值（AIRCRAFT_TYPE / DEFAULT）沿用原有展示，不在这里改写口径。
export const PRICE_MATCH_TEXT = {
    PINNED: "按订单钉住",
};

export const UNBIND_ERROR_TEXT = {
    BOOKING_CONTRACT_UNBIND_BLOCKED_BY_RECEIVABLE: "该订单已生成应收，不能解绑；如需更换合同请用改绑",
    BOOKING_CONTRACT_NOT_BOUND: "该订单当前未绑定合同",
    BOOKING_LINKED_TO_TRANSPORT_BOOKING: "该订单已挂在订舱上，请到订舱取消挂接或换挂",
};

export const BATCH_ACTION_TEXT = {
    CREATE_PREVIEW: "待生成",
    CREATED: "已生成",
    REUSED: "已存在",
    EXCEPTION_PREVIEW: "异常预览",
    EXCEPTION: "已标异常",
    SKIP_MISSING_CONTRACT: "未绑合同",
    SKIP_CONTRACT_MISMATCH: "合同不匹配",
    SKIP_AD_HOC: "一班一议跳过",
    ERROR: "失败",
};

export const FUEL_PREVIEW_STATUS_TEXT = {
    READY: "可生成",
    REUSED: "已存在",
    ERROR: "需处理",
};

export const ROUTE_STATUS_OPTIONS = [
    { label: "全部", value: "" },
    { label: "启用", value: "ACTIVE" },
    { label: "停用", value: "INACTIVE" },
];

export const CONTRACT_STATUS_OPTIONS = [
    { label: "全部", value: "" },
    { label: "草稿", value: "DRAFT" },
    { label: "生效中", value: "ACTIVE" },
    { label: "已过期", value: "EXPIRED" },
    { label: "已终止", value: "TERMINATED" },
];

const CONTRACT_STATUS_EDIT_ALLOWED = {
    DRAFT: ["DRAFT", "ACTIVE", "EXPIRED", "TERMINATED"],
    ACTIVE: ["ACTIVE", "EXPIRED", "TERMINATED"],
    EXPIRED: ["EXPIRED", "ACTIVE", "TERMINATED"],
    TERMINATED: ["TERMINATED"],
};

export function contractStatusEditOptions(currentStatus) {
    const allowed = CONTRACT_STATUS_EDIT_ALLOWED[currentStatus] || ["DRAFT"];
    return CONTRACT_STATUS_OPTIONS.filter((item) => allowed.includes(item.value));
}

export const RECEIVABLE_STATUS_OPTIONS = [
    { label: "全部", value: "" },
    { label: "待开票", value: "PENDING_INVOICE" },
    { label: "已开票", value: "INVOICED" },
    { label: "部分收款", value: "PARTIALLY_PAID" },
    { label: "已收款", value: "PAID" },
    { label: "匹配异常", value: "EXCEPTION" },
    { label: "已取消", value: "CANCELLED" },
];

export const RECEIVABLE_TYPE_OPTIONS = [
    { label: "全部类型", value: "" },
    { label: "基础运价", value: "BASE_FARE" },
    { label: "燃油调整", value: "FUEL_SURCHARGE" },
    { label: "额外费用", value: "EXTRA_FEE" },
];

export const RECEIVABLE_EXCEPTION_OPTIONS = [
    { label: "全部", value: "" },
    { label: "仅异常", value: "YES" },
    { label: "不含异常", value: "NO" },
];

// 已开票/收款的应收锁定，不可再改绑合同；待开票（含金额 0 的异常）允许改绑
export const RECEIVABLE_LOCKED_STATUSES = ["INVOICED", "PARTIALLY_PAID", "PAID"];
export const isReceivableRebindable = (record) =>
    record?.type === "BASE_FARE" &&
    !record?.is_locked &&
    !RECEIVABLE_LOCKED_STATUSES.includes(record?.status) &&
    !record?.linked_to_transport_booking;

export const INVOICE_STATUS_OPTIONS = [
    { label: "全部状态", value: "" },
    { label: "已开票", value: "ISSUED" },
    { label: "部分收款", value: "PARTIALLY_PAID" },
    { label: "已收款", value: "PAID" },
    { label: "已作废", value: "VOIDED" },
];

export const INVOICE_TYPE_OPTIONS = [
    { label: "全部类型", value: "" },
    { label: "基础运价预付发票", value: "BASE_WEEKLY" },
    { label: "燃油调整发票", value: "FUEL_MONTHLY" },
    { label: "额外费用发票", value: "EXTRA_FEE" },
    { label: "混合发票", value: "MIXED" },
];

export const INVOICE_CREATE_TYPE_OPTIONS = [
    { label: "基础运价预付发票", value: "BASE_WEEKLY" },
    { label: "燃油调整发票", value: "FUEL_MONTHLY" },
    { label: "额外费用发票", value: "EXTRA_FEE" },
    { label: "混合发票", value: "MIXED" },
];

export const PAYMENT_SOURCE_OPTIONS = [
    { label: "客户打款", value: "CUSTOMER_PAYMENT" },
    { label: "余额抵扣", value: "CREDIT_LEDGER" },
];

export const CUSTOMER_PAYMENT_METHOD_OPTIONS = [
    { label: "客户打款（电汇）", value: "WIRE" },
    { label: "客户打款（支票）", value: "CHECK" },
    { label: "其他", value: "OTHER" },
];

export const CREDIT_PAYMENT_METHOD_OPTIONS = [
    { label: "预付款抵扣", value: "PREPAYMENT_DEDUCTION" },
    { label: "取消航班抵扣", value: "CANCELLED_BOOKING_CREDIT" },
];

export const EXTRA_FEE_TYPE_OPTIONS = [
    { label: "除冰费", value: "DEICING" },
    { label: "地面操作费", value: "GROUND_HANDLING" },
    { label: "停场费", value: "PARKING" },
    { label: "货运操作费", value: "CARGO_HANDLING" },
    { label: "其他", value: "OTHER" },
];

// 币种下拉统一改用组件内 useCurrencies()（后端 COMMON_CURRENCIES），不再前端硬编码

export const AIRCRAFT_OPTIONS = CARGO_AIRCRAFT_TYPE_OPTIONS;

const AIRCRAFT_TEXT_SEPARATOR = "、";

export function parseAircraftText(value) {
    const text = String(value || "").trim();
    if (!text) return [];
    if (AIRCRAFT_OPTIONS.some((item) => item.value === text)) return [text];
    const parts = text.split(/[、，,;/|]+/).map((item) => item.trim()).filter(Boolean);
    return parts.length > 0 ? parts : [text];
}

export function joinAircraftText(values) {
    if (!Array.isArray(values)) {
        const text = String(values || "").trim();
        return text || null;
    }
    const parts = values.map((item) => String(item).trim()).filter(Boolean);
    return parts.length > 0 ? parts.join(AIRCRAFT_TEXT_SEPARATOR) : null;
}
